import React, { useState } from 'react';
import { Row, Form, Input, Col, Popover } from 'antd';
import styles from './index.module.less';
import FormItem from 'common/FormItem';
import CustomButton from 'common/CustomButton';
import { CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import tataPro from '../../images/logo.svg';
import truck_img from '../../images/login-icon.svg';

const ChangePasswordPage = ({ loading, onSubmit }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const prefillUserId = searchParams.get('user_id') || '';
  const fpcToken = searchParams.get('fpc_token') || '';
  const isExpiredReset = searchParams.get('expired') === '1';

  // Expired-password resets also carry an fpc_token but should not show
  // first-login ("welcome"/"temporary password") wording.
  const isFirstLogin = !!fpcToken && !isExpiredReset;

  const [form] = Form.useForm();
  const [data, setData] = useState('');
  const [uCletter, setUCletter] = useState(false);
  const [specialChar, setSpecialChar] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);

  const lengthOk = data?.length >= 8;
  const strengthCount = [lengthOk, uCletter, specialChar].filter(
    Boolean
  ).length;
  const strengthColor = ['#f0f0f0', '#ff4d4f', '#faad14', '#52c41a'][
    strengthCount
  ];
  const strengthLabel = ['', 'Weak', 'Medium', 'Strong'][strengthCount];

  const handleOnChange = (e) => {
    const value = e.target.value;
    setData(value);
    // Correct: check for presence of both cases independently — not adjacency
    setUCletter(/[A-Z]/.test(value) && /[a-z]/.test(value));
    // Correct: check for presence of digit AND special char independently — not adjacency
    setSpecialChar(/\d/.test(value) && /[@$!%*?&]/.test(value));
  };

  const onFinish = (values) => {
    onSubmit({ ...values, ...(fpcToken ? { fpc_token: fpcToken } : {}) });
  };

  return (
    <Row className={styles.mainContainerStyle}>
      <Col xs={2} xl={12}>
        <img className={`${styles.left_logo} mt-5 ml-5 mb-5`} src={tataPro} />
        <img className={`${styles.main_img} mt-5 mb-5`} src={truck_img} />
      </Col>
      <Col xs={22} xl={12}>
        <div className={styles.right_outer_container}>
          <h1
            style={{
              marginTop: '10%',
              marginBottom: '8px',
              fontWeight: 'Bold',
              fontFamily: 'Source Sans Pro'
            }}
          >
            {isFirstLogin ? 'Welcome to Nucleus' : 'Change Password'}
          </h1>
          {isFirstLogin && (
            <p
              style={{
                marginBottom: '24px',
                color: '#595959',
                fontFamily: 'Source Sans Pro'
              }}
            >
              Your account has been set up with a temporary password. Please set
              a permanent password to continue.
            </p>
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={{ user_id: prefillUserId }}
            className={`ant-form ant-form-vertical ${styles.form_width}`}
            onFinish={onFinish}
            autoComplete="off"
          >
            <FormItem
              label="Employee Code"
              name="user_id"
              rules={[{ required: true, message: 'Required field' }]}
            >
              <Input
                placeholder="Enter Employee/User ID"
                disabled={!!prefillUserId}
                autoComplete="off"
              />
            </FormItem>

            <FormItem
              label={isFirstLogin ? 'Temporary Password' : 'Old Password'}
              name="current_password"
              rules={[{ required: true, message: 'Required field' }]}
            >
              <Input.Password
                placeholder={
                  isFirstLogin
                    ? 'Enter your temporary password'
                    : 'Enter old password'
                }
              />
            </FormItem>

            <Popover
              placement="leftTop"
              trigger={[]}
              open={pwFocused}
              content={
                <div style={{ minWidth: 220, fontSize: 12 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 6,
                      color: '#262626'
                    }}
                  >
                    Password must have:
                  </div>
                  <div className={styles.requirements_list}>
                    <span
                      className={`${styles.requirement_item} ${
                        lengthOk ? styles.met : ''
                      }`}
                      style={{ color: lengthOk ? '#52c41a' : '#8c8c8c' }}
                    >
                      {lengthOk ? (
                        <CheckCircleOutlined />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#bfbfbf' }} />
                      )}
                      At least 8 characters
                    </span>
                    <span
                      className={`${styles.requirement_item} ${
                        uCletter ? styles.met : ''
                      }`}
                      style={{ color: uCletter ? '#52c41a' : '#8c8c8c' }}
                    >
                      {uCletter ? (
                        <CheckCircleOutlined />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#bfbfbf' }} />
                      )}
                      Uppercase and lowercase letters
                    </span>
                    <span
                      className={`${styles.requirement_item} ${
                        specialChar ? styles.met : ''
                      }`}
                      style={{ color: specialChar ? '#52c41a' : '#8c8c8c' }}
                    >
                      {specialChar ? (
                        <CheckCircleOutlined />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#bfbfbf' }} />
                      )}
                      A number and a special character (@$!%*?&)
                    </span>
                  </div>
                  <div className={styles.strength_bar}>
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className={styles.segment}
                        style={{
                          background:
                            i < strengthCount ? strengthColor : '#f0f0f0'
                        }}
                      />
                    ))}
                  </div>
                  {strengthLabel ? (
                    <div
                      style={{
                        fontSize: '11px',
                        color: strengthColor,
                        marginTop: '3px',
                        fontWeight: 600,
                        transition: 'color 0.4s ease'
                      }}
                    >
                      {strengthLabel}
                    </div>
                  ) : null}
                </div>
              }
            >
              <FormItem
                label="New Password"
                name="new_password"
                rules={[
                  { required: true, message: 'Required field' },
                  {
                    pattern:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message:
                      'Min 8 characters with at least one uppercase letter, one lowercase letter, one number and one special character (@$!%*?&)'
                  }
                ]}
              >
                <Input.Password
                  placeholder="Enter new password"
                  onChange={handleOnChange}
                  onFocus={() => setPwFocused(true)}
                  onBlur={() => setPwFocused(false)}
                />
              </FormItem>
            </Popover>

            <FormItem
              label="Confirm Password"
              name="confirm_password"
              dependencies={['new_password']}
              rules={[
                { required: true, message: 'Required field' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'The two passwords that you entered do not match!'
                      )
                    );
                  }
                })
              ]}
            >
              <Input.Password placeholder="Enter confirm password" />
            </FormItem>

            <FormItem>
              <CustomButton
                block
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Change Password
              </CustomButton>
            </FormItem>

            {!isFirstLogin && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <a href="/login">Back to Login</a>
              </div>
            )}

            <div
              style={{
                marginTop: '16px',
                padding: '10px 12px',
                background: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #f0f0f0',
                fontSize: '11px',
                color: '#8c8c8c',
                lineHeight: '1.8'
              }}
            >
              <span style={{ fontWeight: 600, color: '#595959' }}>
                Terms &amp; Conditions:{' '}
              </span>
              Passwords expire every 90 days. &nbsp;·&nbsp; Passwords used in
              the last 12 months cannot be reused. &nbsp;·&nbsp; Account locks
              after 3 failed login attempts.
            </div>
          </Form>
        </div>
      </Col>
    </Row>
  );
};

export default ChangePasswordPage;
